import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { enhanceProductImage } from '../../api/ProductMedia';
import { sharedColors, sharedStyles } from '../../utils/Style';
import {
  ProductEnhancementPreset,
  productEnhancementPresetNameMap,
} from '../../interfaces/Product/ProductEnhancementPreset';

type EnhanceImageDialogProps = {
  image?: File;
  onClose: () => void;
  onEnhance: (enhancedImage: File) => void;
};

export const EnhanceImageDialog = ({
  image,
  onClose,
  onEnhance,
}: EnhanceImageDialogProps) => {
  const [enhancedImage, setEnhancedImage] = useState<File | undefined>(
    undefined,
  );
  const [preset, setPreset] = useState<ProductEnhancementPreset>(
    ProductEnhancementPreset.STUDIO,
  );
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEnhancedImage(undefined);
    setAdditionalPrompt('');
  }, [image]);

  const handleEnhance = useCallback(() => {
    if (!image) return;

    setLoading(true);
    enhanceProductImage(image, preset, additionalPrompt)
      .then(setEnhancedImage)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [image, preset, additionalPrompt]);

  const resolvedImage = enhancedImage ?? image;
  const imageURL = resolvedImage ? URL.createObjectURL(resolvedImage) : '';

  return (
    <Dialog open={!!image} onClose={onClose}>
      <DialogTitle>Resmi İyileştir</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: 400,
        }}
      >
        <Box
          component='img'
          src={imageURL}
          alt='Resim'
          sx={{
            width: 400,
            height: 300,
            objectFit: 'contain',
            borderColor: sharedColors.gray3,
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        />

        <FormControl size='small' disabled={loading} fullWidth>
          <InputLabel>Ön Ayar</InputLabel>

          <Select
            label='Ön Ayar'
            value={preset}
            onChange={(e) =>
              setPreset(e.target.value as ProductEnhancementPreset)
            }
          >
            {Object.entries(productEnhancementPresetNameMap).map(
              ([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>

        <TextField
          label='Ek notlar'
          variant='outlined'
          multiline
          size='small'
          minRows={3}
          disabled={loading}
          value={additionalPrompt}
          onChange={(e) => setAdditionalPrompt(e.target.value)}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Button
            variant='outlined'
            color='primary'
            startIcon={<AutoAwesomeIcon />}
            disabled={loading}
            onClick={handleEnhance}
            sx={sharedStyles.buttonText}
          >
            İyileştir
          </Button>

          {loading && <CircularProgress size={24} />}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 1.5 }}>
        <Button
          variant='contained'
          color='inherit'
          onClick={onClose}
          sx={sharedStyles.buttonText}
        >
          İptal
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!enhancedImage}
          onClick={() => {
            if (enhancedImage) {
              onEnhance(enhancedImage);
            }
            onClose();
          }}
          sx={sharedStyles.buttonText}
        >
          Onayla
        </Button>
      </DialogActions>
    </Dialog>
  );
};
